import axios from 'axios';
import {
  getAccessToken,
  setAccessToken,
  logout,
  registerServerLogout,
} from '../utils/auth.js';

// Configure the base URL for your backend API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: true,
});

const axiosPlain = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  timeout: 30000,
  withCredentials: true,
});

axiosPlain.interceptors.response.use(
  response => {
    if (response.data?.code === 'MAINTENANCE') {
      if (window.location.pathname !== '/manutencao') {
        window.location.href = '/manutencao';
      }
      return Promise.reject(new Error('MAINTENANCE'));
    }
    return response;
  },
  error => {
    if (error.response?.data?.code === 'MAINTENANCE') {
      if (window.location.pathname !== '/manutencao') {
        window.location.href = '/manutencao';
      }
    }
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue = [];
/** Single-flight refresh shared by bootstrap, interceptor, and refreshToken(). */
let refreshPromise = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const extractAccessToken = data => {
  if (typeof data === 'string') return data;
  if (data && typeof data.access_token === 'string') return data.access_token;
  if (data && typeof data.token === 'string') return data.token;
  return null;
};

/**
 * Refresh via HttpOnly cookie (no refresh token in JS/body).
 * Single-flight: concurrent callers (bootstrap, 401 interceptor, Strict Mode) share one request.
 * Returns new access token string or null.
 */
export const tryRefreshSession = () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const { data } = await axiosPlain.post('/api/v1/auth/refresh');
      const newToken = extractAccessToken(data);
      if (!newToken) return null;
      setAccessToken(newToken);
      return newToken;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/** Best-effort server logout — clears HttpOnly refresh cookie (backend contract). */
export const logoutRequest = async () => {
  await axiosPlain.post('/api/v1/auth/logout');
};

registerServerLogout(logoutRequest);

apiClient.interceptors.request.use(
  config => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  response => {
    if (response.data?.code === 'MAINTENANCE') {
      if (window.location.pathname !== '/manutencao') {
        window.location.href = '/manutencao';
      }
      return Promise.reject(new Error('MAINTENANCE'));
    }
    return response;
  },
  async error => {
    if (error.response?.data?.code === 'MAINTENANCE') {
      if (window.location.pathname !== '/manutencao') {
        window.location.href = '/manutencao';
      }
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    const status = error.response?.status;

    // 403 = authorization failure (UX should handle). Do not refresh or logout.
    if (status === 403) {
      return Promise.reject(error);
    }

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/logout')
    ) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return apiClient(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await tryRefreshSession();

        if (!newToken) {
          processQueue(error, null);
          logout();
          return Promise.reject(error);
        }

        apiClient.defaults.headers.common['Authorization'] =
          'Bearer ' + newToken;
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;

        processQueue(null, newToken);
        return apiClient(originalRequest);
      } catch (err) {
        processQueue(err, null);
        logout();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const login = async (email, password, options = {}) => {
  try {
    const body = {
      email,
      password,
    };
    if (typeof options.remember_me === 'boolean') {
      body.remember_me = options.remember_me;
    }
    // Prefer plain client so a failed login never triggers refresh/logout side effects
    const response = await axiosPlain.post('/api/v1/auth/login', body);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao fazer login'
    );
  }
};

export const getCurrentUser = async () => {
  return fetchCurrentUserProfile();
};

/** Profile from /auth/me — source of truth for UX roles after login/bootstrap */
export const fetchCurrentUserProfile = async () => {
  try {
    const response = await apiClient.get('/api/v1/auth/me');
    const userData = response.data;

    // If cd_role is not present, fetch roles and attach it to prevent hardcoded ID dependency
    if (userData && !userData.cd_role) {
      try {
        const roles = await getRoles();
        const rolesList = Array.isArray(roles)
          ? roles
          : roles.items || roles.roles || roles.data || [];
        const userRole = rolesList.find(r => r.id_role === userData.id_role);
        if (userRole) {
          userData.cd_role = userRole.cd_role;
        }
      } catch (roleError) {
        console.error('Error fetching roles to map user role code:', roleError);
      }
    }

    return userData;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao buscar informações do usuário'
    );
  }
};

export const updateUserProfile = async userData => {
  try {
    const response = await apiClient.put('/api/v1/auth/me', userData);
    return response.data;
  } catch (error) {
    // Fallback if auth/me PUT not supported, try using generic update with ID if available in context,
    // but here we just throw or try to handle
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao atualizar informações do usuário'
    );
  }
};

/** Refresh via HttpOnly cookie. Optional body arg ignored (compat). */
export const refreshToken = async () => {
  const token = await tryRefreshSession();
  if (!token) {
    throw new Error('Erro ao renovar token');
  }
  return { access_token: token };
};

export const getLandingPage = async () => {
  try {
    const response = await apiClient.get('api/landing-page');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar a landing page'
    );
  }
};

export const getNextWorkshopDate = async () => {
  try {
    const data = await getLandingPage();
    return { date: data.curso?.start_date || '' };
  } catch (error) {
    throw new Error(error.message || 'Erro ao carregar a data do curso');
  }
};

export const getUsers = async (
  page = 1,
  limit = 10,
  search = '',
  sort = 'id_usuario',
  sort_order = 'asc'
) => {
  try {
    const response = await apiClient.get('/api/v1/usuarios/', {
      params: { page, limit, search, sort, sort_order },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar usuários'
    );
  }
};

export const getAllUsers = async () => {
  try {
    const response = await apiClient.get('/api/v1/usuarios/');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar usuários'
    );
  }
};

export const getUserById = async userId => {
  try {
    const response = await apiClient.get(`/api/v1/usuarios/${userId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar usuário'
    );
  }
};

export const createUserMember = async userData => {
  try {
    const response = await apiClient.post('/api/v1/usuarios/sign-up', userData);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao criar usuário'
    );
  }
};

export const createUser = async userData => {
  try {
    const response = await apiClient.post('/api/v1/usuarios/', userData);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao criar usuário'
    );
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const response = await apiClient.patch(
      `/api/v1/usuarios/${userId}`,
      userData
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao atualizar usuário'
    );
  }
};

export const updateUserData = async (userId, userData) => {
  try {
    const response = await apiClient.put(
      `/api/v1/usuarios/${userId}`,
      userData
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao atualizar dados do usuário'
    );
  }
};

export const deleteUser = async userId => {
  try {
    const response = await apiClient.delete(`/api/v1/usuarios/${userId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao deletar usuário'
    );
  }
};

export const getRoles = async () => {
  try {
    const response = await apiClient.get('/api/v1/roles/list');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar roles'
    );
  }
};

export const getRoleById = async roleId => {
  try {
    const response = await apiClient.get(`/api/v1/roles/${roleId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar role'
    );
  }
};

export const getInstitutions = async () => {
  try {
    const response = await apiClient.get('/api/v1/instituicao/list');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar instituições'
    );
  }
};

export const getOrganizationUsers = async () => {
  try {
    const data = await getAllUsers();
    return data;
  } catch (error) {
    throw new Error(
      error.message || 'Erro ao carregar usuários da organização'
    );
  }
};

export const setMonitorInviteLimit = async (monitorId, limit) => {
  try {
    const response = await apiClient.post('/api/v1/monitor-invite/limit', {
      id_usuario_monitor: monitorId,
      qt_limite_invites: limit,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao definir limite de convites'
    );
  }
};

export const getMonitorInviteLimit = async monitorId => {
  try {
    const response = await apiClient.get(
      `/api/v1/monitor-invite/limit/${monitorId}`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao buscar limite de convites'
    );
  }
};

export const getMonitorInviteStats = async monitorId => {
  try {
    const response = await apiClient.get(
      `/api/v1/monitor-invite/stats/${monitorId}`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao buscar estatísticas de convites'
    );
  }
};

export const getAllMonitorInviteLimits = async () => {
  try {
    const response = await apiClient.get('/api/v1/monitor-invite/limits');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao buscar limites de convites'
    );
  }
};

export const getMonitorsWithoutLimits = async () => {
  try {
    const response = await apiClient.get(
      '/api/v1/monitor-invite/monitors-without-limits'
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao buscar monitores sem limite'
    );
  }
};

export const inviteUsers = async (institutionId, emails) => {
  try {
    const response = await apiClient.post(
      '/api/v1/instituicao-acesso/gerar-link',
      {
        id_instituicao: institutionId,
        nm_emails_usuario_invite: emails,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 409 && error.response?.data) {
      throw error.response.data;
    }
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao enviar convites'
    );
  }
};

export const deleteInvite = async instituicaoAcessoId => {
  try {
    const response = await apiClient.delete(
      `/api/v1/instituicao-acesso/${instituicaoAcessoId}`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao deletar convite'
    );
  }
};

export const getInvitedUsers = async () => {
  try {
    const response = await apiClient.get('/api/v1/instituicao-acesso/list');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao buscar convites enviados'
    );
  }
};

export const validateAccessLink = async token => {
  try {
    const response = await apiClient.post(
      '/api/v1/instituicao-acesso/validar-link',
      { token }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao validar link de acesso'
    );
  }
};

export const getCoordinatorsAndMonitors = async () => {
  try {
    const [users, roles] = await Promise.all([getAllUsers(), getRoles()]);

    const roleMap = {};
    const rolesList = Array.isArray(roles)
      ? roles
      : roles.items || roles.roles || roles.data || [];
    rolesList.forEach(role => {
      roleMap[role.id_role] = role.cd_role;
    });

    const usersList = Array.isArray(users)
      ? users
      : users.items || users.usuarios || users.data || [];

    return usersList
      .filter(user => {
        const roleCode = roleMap[user.id_role];
        return roleCode === 'COR' || roleCode === 'MON';
      })
      .map(user => ({
        ...user,
        cd_role: roleMap[user.id_role],
      }));
  } catch (error) {
    throw new Error(
      error.message || 'Erro ao carregar coordenadores e monitores'
    );
  }
};

export const createCoordinatorOrMonitor = async userData => {
  try {
    const roles = await getRoles();
    const roleMap = {};
    roles.forEach(role => {
      roleMap[role.cd_role] = role.id_role;
    });

    const roleId = roleMap[userData.role];
    if (!roleId) {
      throw new Error(`Role ${userData.role} não encontrada`);
    }

    const backendData = {
      id_role: roleId,
      nm_usuario: userData.name,
      nm_email: userData.email,
      pw_senha: userData.password,
      sg_aluno: 'S',
      dt_nascimento: new Date().toISOString().split('T')[0],
      cd_genero: 'O',
      bo_liberado: true,
      id_instituicao: userData.institution,
      id_tipo_instituicao: userData.id_tipo_instituicao || 1,
      id_area_atuacao: userData.id_area_atuacao || 1,
      id_cargo: userData.id_cargo || 1,
    };
    return await createUser(backendData);
  } catch (error) {
    throw new Error(error.message || 'Erro ao criar usuário');
  }
};

export const updateSimuladorUrl = async url => {
  try {
    const response = await apiClient.patch(
      '/api/v1/curso-conteudo/simulador-url',
      {
        url,
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao atualizar URL do simulador'
    );
  }
};

export const getSimuladorUrl = async () => {
  try {
    const response = await apiClient.get(
      '/api/v1/curso-conteudo/simulador-url'
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao buscar URL do simulador'
    );
  }
};

// User Courses
export const getUserCourses = async () => {
  try {
    const response = await apiClient.get('/api/v1/curso-usuario/');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar cursos do usuário'
    );
  }
};

export const getUserCourseModules = async courseUserId => {
  try {
    const response = await apiClient.get('/api/v1/curso-modulo-usuario/', {
      params: { curso_usuario_id: courseUserId },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar módulos do curso'
    );
  }
};

export const getUserCourseContents = async (
  courseUserId,
  { slim = false, page = 1, limit = 100 } = {}
) => {
  try {
    const params = {
      curso_usuario_id: courseUserId,
      page,
      limit,
    };
    if (slim) params.slim = true;

    const response = await apiClient.get('/api/v1/curso-conteudo-usuario/', {
      params,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar conteúdos do curso'
    );
  }
};

export const getConteudoUsuarioById = async conteudoUsuarioId => {
  try {
    const response = await apiClient.get(
      `/api/v1/curso-conteudo-usuario/${conteudoUsuarioId}`
    );
    return response.data;
  } catch (error) {
    const err = new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar registro de conteúdo'
    );
    err.status = error.response?.status;
    err.response = error.response;
    throw err;
  }
};

export const saveVideoProgress = async (conteudoUsuarioId, tempoSegundos) => {
  try {
    const response = await apiClient.patch(
      `/api/v1/curso-conteudo-usuario/${conteudoUsuarioId}/progresso-video`,
      { tempo_segundos: Math.floor(tempoSegundos) }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao salvar progresso do vídeo'
    );
  }
};

export const getTrilhaAcompanhamento = async courseUserId => {
  try {
    const response = await apiClient.get(
      '/api/v1/curso-conteudo-usuario/trilha-acompanhamento',
      {
        params: { curso_usuario_id: courseUserId },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar trilha de acompanhamento'
    );
  }
};

// Progress Endpoints

export const getCourseProgress = async (courseId, userId = null) => {
  try {
    const params = {};
    if (courseId) params.curso_usuario_id = courseId;
    if (userId) params.user_id = userId;

    const response = await apiClient.get('/api/v1/progresso/curso', {
      params,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar progresso do curso'
    );
  }
};

export const startCourse = async curso_usuario_id => {
  try {
    const response = await apiClient.post('/api/v1/progresso/curso/iniciar', {
      curso_usuario_id,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao iniciar o curso'
    );
  }
};
export const startModuleProgress = async curso_modulo_usuario_id => {
  try {
    const response = await apiClient.post('/api/v1/progresso/modulo/iniciar', {
      curso_modulo_usuario_id,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao iniciar o módulo'
    );
  }
};
export const getModuleProgress = async (moduleId, userId = null) => {
  try {
    const params = {};
    if (moduleId) params.curso_modulo_usuario_id = moduleId;
    if (userId) params.user_id = userId;

    const response = await apiClient.get('/api/v1/progresso/modulo', {
      params,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar progresso do módulo'
    );
  }
};

export const getContentProgress = async (contentId, userId = null) => {
  try {
    const params = {};
    if (contentId) params.curso_conteudo_usuario_id = contentId;
    if (userId) params.user_id = userId;

    const response = await apiClient.get('/api/v1/progresso/conteudo', {
      params,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar progresso do conteúdo'
    );
  }
};

export const getEvaluationGrade = async (evaluationId, userId = null) => {
  try {
    const params = {};
    if (evaluationId) params.curso_avaliacao_usuario_id = evaluationId;
    if (userId) params.user_id = userId;

    const response = await apiClient.get('/api/v1/progresso/avaliacao', {
      params,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar nota da avaliação'
    );
  }
};

export const getAvaliacaoByConteudo = async conteudoId => {
  try {
    const response = await apiClient.get('/api/v1/curso-avaliacao/', {
      params: { conteudo_id: conteudoId },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar avaliação'
    );
  }
};

// getEvaluationResult removed as per user request (endpoint /api/v1/curso-avaliacao/resultado-usuario is incorrect)
export const getEvaluationQuestionsStatus = async ({
  id_curso_tentativa_usuario,
  id_curso_avaliacao,
  curso_avaliacao_questao_id,
}) => {
  try {
    const params = {};
    if (id_curso_tentativa_usuario)
      params.id_curso_tentativa_usuario = id_curso_tentativa_usuario;
    if (id_curso_avaliacao) params.id_curso_avaliacao = id_curso_avaliacao;
    if (curso_avaliacao_questao_id)
      params.curso_avaliacao_questao_id = curso_avaliacao_questao_id;

    // Prevent aggressive browser caching of GET requests when polling
    params._t = new Date().getTime();

    const response = await apiClient.get(
      '/api/v1/curso-avaliacao-questao-usuario/',
      { params }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar gabarito da avaliação'
    );
  }
};

export const getAvaliacaoQuestoes = async avaliacaoId => {
  try {
    const response = await apiClient.get('/api/v1/curso-avaliacao-questao/', {
      params: { avaliacao_id: avaliacaoId },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar questões da avaliação'
    );
  }
};

export const getAvaliacaoTentativas = async (
  cursoConteudoUsuarioId,
  cursoAvaliacaoId = null
) => {
  try {
    const params = { curso_conteudo_usuario_id: cursoConteudoUsuarioId };
    if (cursoAvaliacaoId) params.curso_avaliacao_id = cursoAvaliacaoId;
    const response = await apiClient.get('/api/v1/curso-avaliacao-usuario/', {
      params,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar tentativas da avaliação'
    );
  }
};

export const startAvaliacao = async data => {
  try {
    const response = await apiClient.post(
      '/api/v1/curso-avaliacao-usuario/',
      data
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao iniciar tentativa de avaliação'
    );
  }
};

export const submitEvaluationAnswer = async data => {
  try {
    const response = await apiClient.post(
      '/api/v1/curso-avaliacao-questao-usuario/',
      data
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao enviar resposta da avaliação'
    );
  }
};

// Progress Updates
export const startContent = async contentUserId => {
  try {
    const response = await apiClient.post(
      '/api/v1/progresso/conteudo/iniciar',
      {
        curso_conteudo_usuario_id: contentUserId,
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao iniciar conteúdo'
    );
  }
};

export const finishContent = async contentUserId => {
  try {
    const response = await apiClient.post(
      '/api/v1/progresso/conteudo/finalizar',
      {
        curso_conteudo_usuario_id: contentUserId,
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao finalizar conteúdo'
    );
  }
};

export const finishEvaluation = async payload => {
  try {
    const response = await apiClient.post(
      '/api/v1/progresso/avaliacao/finalizar',
      payload
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao finalizar avaliação'
    );
  }
};

export const finishModuleProgress = async curso_modulo_usuario_id => {
  try {
    const response = await apiClient.post(
      '/api/v1/progresso/modulo/finalizar',
      {
        curso_modulo_usuario_id,
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao finalizar o módulo'
    );
  }
};

export const finishCourseProgress = async curso_usuario_id => {
  try {
    const response = await apiClient.post('/api/v1/progresso/curso/finalizar', {
      curso_usuario_id,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao finalizar o curso'
    );
  }
};

export const getCertificateTest = async (
  userId,
  courseId,
  courseVersionId = null
) => {
  try {
    const response = await apiClient.post(
      '/api/certificados/',
      {
        id_usuario: userId,
        id_curso: courseId,
        id_curso_versao: courseVersionId,
      },
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao gerar certificado'
    );
  }
};

export const getCertificateData = async (userId, courseId) => {
  try {
    const response = await apiClient.get('/api/certificados/', {
      params: { id_usuario: userId, id_curso: courseId },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao buscar dados do certificado'
    );
  }
};

export const validateCertificate = async validationCode => {
  try {
    const response = await apiClient.get(
      `/api/certificados/validar/${validationCode}`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao validar certificado'
    );
  }
};

export const getCourseById = async courseId => {
  try {
    const response = await apiClient.get(`/api/v1/curso/${courseId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar detalhes do curso'
    );
  }
};

export const getTurmas = async (courseId, sgModalidade = null) => {
  try {
    const params = {};
    if (courseId) params.id_curso = courseId;
    if (sgModalidade) params.sg_modalidade = sgModalidade;

    const response = await apiClient.get('/api/v1/turma', { params });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar turmas'
    );
  }
};

export const registerUserCourse = async data => {
  try {
    const response = await apiClient.post('/api/v1/curso-usuario', data);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao realizar inscrição'
    );
  }
};

export const getStatusMatricula = async () => {
  try {
    const response = await apiClient.get(
      '/api/v1/turma-usuario/status-matricula'
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao buscar status de matrícula'
    );
  }
};

export const getTurmasUsuario = async () => {
  try {
    const response = await apiClient.get('/api/v1/turma-usuario');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao buscar turmas do usuário'
    );
  }
};

export const inscreverUsuarioTurma = async (id_turma, id_usuario) => {
  try {
    const response = await apiClient.post('/api/v1/turma-usuario/inscricao', {
      id_turma,
      id_usuario,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao se inscrever na turma'
    );
  }
};

export const getCourses = async (page = 1, limit = 10, search = '') => {
  try {
    const response = await apiClient.get('/api/v1/curso/', {
      params: { page, limit, search },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar cursos'
    );
  }
};

export const getDashboardMetrics = async (idTurma = null, config = {}) => {
  try {
    const params = {};
    if (idTurma) params.id_turma = idTurma;
    const response = await apiClient.get('/api/v1/dashboard/metricas/', {
      params,
      timeout: 30000,
      ...config,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar métricas do dashboard'
    );
  }
};

export const getDashboardMemberMetrics = async (
  idTurma = null,
  page = 1,
  limit = 10,
  sort = null,
  sortOrder = 'asc',
  config = {}
) => {
  try {
    const params = { page, limit };
    if (idTurma) params.id_turma = idTurma;
    if (sort) {
      params.sort = sort;
      params.sort_order = sortOrder;
    }
    const response = await apiClient.get(
      '/api/v1/dashboard/metricas-membros/',
      {
        params,
        timeout: 30000,
        ...config,
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar métricas de membros do dashboard'
    );
  }
};

export const generateTurmaReport = async idTurma => {
  try {
    const response = await apiClient.post(
      '/api/relatorios/turma',
      { id_turma: idTurma },
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao gerar relatório da turma'
    );
  }
};

export const generateTurmasReport = async () => {
  try {
    const response = await apiClient.post(
      '/api/relatorios/turmas',
      {},
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao gerar relatório das turmas'
    );
  }
};

export const forgotPassword = async email => {
  try {
    const response = await apiClient.post('/api/v1/auth/forgot-password', {
      email,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao solicitar redefinição de senha'
    );
  }
};

export const resetPassword = async (
  token,
  senha_nova,
  senha_nova_confirmacao
) => {
  try {
    const response = await apiClient.post('/api/v1/auth/reset-password', {
      token,
      senha_nova,
      senha_nova_confirmacao,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao redefinir senha'
    );
  }
};

export const getPublicCourseModules = async cursoVersaoId => {
  try {
    const response = await apiClient.get('/api/v1/curso-modulo/', {
      params: { curso_versao_id: cursoVersaoId },
    });
    return response.data;
  } catch {
    return { modulos: [] };
  }
};

// Feedback Endpoints
export const getFeedbackQuestoes = async curso_conteudo_usuario_id => {
  try {
    const response = await apiClient.get(
      '/api/v1/curso-feed/curso-feed-questoes',
      {
        params: { curso_conteudo_usuario_id },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar questões do feedback'
    );
  }
};

export const submitFeedbackAnswer = async data => {
  try {
    const response = await apiClient.post(
      '/api/v1/curso-feed-questao-usuario/',
      data
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao enviar resposta do feedback'
    );
  }
};

export const createFeedback = async data => {
  try {
    const response = await apiClient.post('/api/v1/curso-feed/', data);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao enviar feedback'
    );
  }
};

export const getFeedbacks = async (moduloId = null, usuarioId = null) => {
  try {
    const params = {};
    if (moduloId) params.modulo_id = moduloId;
    if (usuarioId) params.usuario_id = usuarioId;
    const response = await apiClient.get('/api/v1/curso-feed/', { params });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar feedbacks'
    );
  }
};

export const getAllFeedbackQuestions = async () => {
  try {
    const response = await apiClient.get('/api/v1/curso-feed/');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar questões de feedback'
    );
  }
};

export const getAllFeedbackAlternatives = async () => {
  try {
    const response = await apiClient.get('/api/v1/curso-feed-questao/');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar alternativas de feedback'
    );
  }
};

export const getAllFeedbackAnswers = async () => {
  try {
    const response = await apiClient.get('/api/v1/curso-feed-questao-usuario/');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar respostas de feedback'
    );
  }
};

export const getResultadosFeedback = async (idTurma = null) => {
  try {
    const params = {};
    if (idTurma) {
      params.id_turma = idTurma;
    }
    const response = await apiClient.get(
      '/api/v1/curso-feed-questao-usuario/resultados-feedback',
      { params }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar resultados de feedback'
    );
  }
};

export const getFeedbackById = async feedId => {
  try {
    const response = await apiClient.get(`/api/v1/curso-feed/${feedId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar feedback'
    );
  }
};

export const updateFeedback = async (feedId, data) => {
  try {
    const response = await apiClient.patch(
      `/api/v1/curso-feed/${feedId}`,
      data
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao atualizar feedback'
    );
  }
};

export const deleteFeedback = async feedId => {
  try {
    const response = await apiClient.delete(`/api/v1/curso-feed/${feedId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao excluir feedback'
    );
  }
};

// --- Admin Course Management ---

export const createCourse = async formData => {
  try {
    const response = await apiClient.post('/api/v1/curso/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao criar curso'
    );
  }
};

export const updateCourse = async (courseId, data) => {
  try {
    const response = await apiClient.patch(`/api/v1/curso/${courseId}`, data);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao atualizar curso'
    );
  }
};

export const deleteCourse = async courseId => {
  try {
    const response = await apiClient.delete(`/api/v1/curso/${courseId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao excluir curso'
    );
  }
};

// --- Admin Module Management ---

export const getCourseModulesAdmin = async (
  cursoVersaoId,
  search = '',
  page = 1,
  limit = 100
) => {
  try {
    const response = await apiClient.get('/api/v1/curso-modulo/', {
      params: { curso_versao_id: cursoVersaoId, search, page, limit },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao listar módulos'
    );
  }
};

export const getModuleById = async moduleId => {
  try {
    const response = await apiClient.get(`/api/v1/curso-modulo/${moduleId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar módulo'
    );
  }
};

export const createModule = async formData => {
  try {
    const response = await apiClient.post('/api/v1/curso-modulo/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao criar módulo'
    );
  }
};

export const updateModule = async (moduleId, data) => {
  try {
    const response = await apiClient.patch(
      `/api/v1/curso-modulo/${moduleId}`,
      data
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao atualizar módulo'
    );
  }
};

export const deleteModule = async moduleId => {
  try {
    const response = await apiClient.delete(`/api/v1/curso-modulo/${moduleId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao excluir módulo'
    );
  }
};

// --- Admin Content Management ---

export const getCourseContentsAdmin = async (
  moduloId,
  includeVimeo = false,
  search = '',
  page = 1,
  limit = 100
) => {
  try {
    const response = await apiClient.get('/api/v1/curso-conteudo/', {
      params: {
        modulo_id: moduloId,
        include_vimeo: includeVimeo,
        search,
        page,
        limit,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao listar conteúdos'
    );
  }
};

export const getCourseContentsByModule = async moduloId => {
  try {
    const response = await apiClient.get('/api/v1/curso-conteudo/', {
      params: { modulo_id: moduloId },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao listar conteúdos do módulo'
    );
  }
};

export const getCourseModulesByVersion = async cursoVersaoId => {
  try {
    const response = await apiClient.get('/api/v1/curso-modulo/', {
      params: { curso_versao_id: cursoVersaoId },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao listar módulos'
    );
  }
};

export const getContentById = async (contentId, includeVimeo = true) => {
  try {
    const response = await apiClient.get(
      `/api/v1/curso-conteudo/${contentId}`,
      {
        params: { include_vimeo: includeVimeo },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar conteúdo'
    );
  }
};

export const createContent = async formData => {
  try {
    const response = await apiClient.post('/api/v1/curso-conteudo/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao criar conteúdo'
    );
  }
};

export const updateContent = async (contentId, data) => {
  try {
    const response = await apiClient.patch(
      `/api/v1/curso-conteudo/${contentId}`,
      data
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao atualizar conteúdo'
    );
  }
};

export const deleteContent = async contentId => {
  try {
    const response = await apiClient.delete(
      `/api/v1/curso-conteudo/${contentId}`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao excluir conteúdo'
    );
  }
};

// --- Tipo de Instituição, Área de Atuação, Cargo ---

export const getTiposInstituicao = async () => {
  try {
    const response = await axiosPlain.get('/api/v1/tipo-instituicao/');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar tipos de instituição'
    );
  }
};

export const getAreasAtuacao = async (tipoInstituicaoId = null) => {
  try {
    const params = {};
    if (tipoInstituicaoId) params.id_tipo_instituicao = tipoInstituicaoId;
    const response = await axiosPlain.get('/api/v1/area-atuacao/', { params });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar áreas de atuação'
    );
  }
};

export const getCargos = async (tipoInstituicaoId = null) => {
  try {
    const params = {};
    if (tipoInstituicaoId) params.id_tipo_instituicao = tipoInstituicaoId;
    const response = await axiosPlain.get('/api/v1/cargo/', { params });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar cargos'
    );
  }
};

// --- Questionario (Quiz/Form) ---

export const getQuestionarioForm = async contentId => {
  try {
    const response = await apiClient.get(
      '/api/v1/curso-questionario-usuario/formulario',
      {
        params: { id_curso_conteudo_usuario: contentId },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar questionário'
    );
  }
};

export const submitQuestionarioAnswer = async data => {
  try {
    const response = await apiClient.post(
      '/api/v1/curso-questionario-questao-usuario/',
      data
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao enviar resposta do questionário'
    );
  }
};

export const exportQuestionarioGiz = async (idTurma = null) => {
  try {
    const params = {};
    if (idTurma) params.id_turma = idTurma;
    const response = await apiClient.get(
      '/api/relatorios/questionario-giz/export',
      { params, responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao exportar questionário GIZ'
    );
  }
};

export const getCourseStructure = async (
  cursoVersaoId,
  includeVimeo = true
) => {
  try {
    const response = await apiClient.get(
      `/api/v1/curso/versao/${cursoVersaoId}/estrutura`,
      {
        params: { include_vimeo: includeVimeo },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao carregar estrutura do curso'
    );
  }
};

export default apiClient;
